'use client';

import { useState } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { Building2, Check, ChevronDown, Loader2 } from 'lucide-react';

/**
 * TenantSwitcher - CR-005 Multi-Tenant Switching Component
 *
 * Dropdown component for top navigation that allows users to:
 * - View current active tenant
 * - See all available tenants
 * - Switch between tenants without logout
 *
 * Features:
 * - Dropdown menu with current tenant display
 * - Visual checkmark for active tenant
 * - Shows role for each tenant
 * - Smooth tenant switching with loading state
 * - Accessible keyboard navigation
 *
 * Usage:
 * ```tsx
 * <TenantSwitcher />
 * ```
 */

export function TenantSwitcher() {
  const { currentTenant, availableTenants, switchTenant, loading } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Don't show if user has only one tenant or no tenants
  if (!availableTenants || availableTenants.length <= 1) {
    return null;
  }

  const handleSwitchTenant = async (tenantId: string) => {
    if (tenantId === currentTenant?.tenant_id) {
      setIsOpen(false);
      return;
    }

    try {
      setSwitching(true);
      await switchTenant(tenantId);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      setSwitching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, tenantId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSwitchTenant(tenantId);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      {/* Current Tenant Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Switch tenant"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Building2 className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        <div className="hidden md:block text-left">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {currentTenant?.tenant_name || 'Select Tenant'}
          </div>
          {currentTenant?.role && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {currentTenant.role.replace('_', ' ')}
            </div>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 text-gray-600 dark:text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Switch Organization
              </div>

              <div className="space-y-1">
                {availableTenants.map((tenant) => {
                  const isCurrent = tenant.tenant_id === currentTenant?.tenant_id;

                  return (
                    <button
                      key={tenant.tenant_id}
                      onClick={() => handleSwitchTenant(tenant.tenant_id)}
                      onKeyDown={(e) => handleKeyDown(e, tenant.tenant_id)}
                      disabled={switching || isCurrent}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                        isCurrent
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100'
                      } ${
                        switching ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                      role="menuitem"
                    >
                      {/* Checkmark or Building Icon */}
                      <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                        {isCurrent ? (
                          <Check className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        ) : (
                          <Building2 className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                        )}
                      </div>

                      {/* Tenant Info */}
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {tenant.tenant_name}
                          </span>
                          {tenant.is_default && (
                            <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-1.5 py-0.5 rounded">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {tenant.role.replace('_', ' ')}
                        </div>
                      </div>

                      {/* Loading Spinner */}
                      {switching && isCurrent && (
                        <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2 px-3 py-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {availableTenants.length} organization{availableTenants.length !== 1 ? 's' : ''} available
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Global Loading Overlay */}
      {(loading || switching) && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 flex items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Switching organization...
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
